import React, { useState } from "react";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Tag,
  Upload,
  X,
  Plus,
} from "lucide-react";
import { eventsAPI } from "../services/api";
import { toast } from "react-toastify";

const EventCreationForm = ({ onSuccess, onCancel }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    startDate: "",
    endDate: "",
    time: "",
    location: {
      venue: "",
      address: "",
      city: "",
    },
    maxAttendees: 100,
    tags: [],
    images: [],
    trailerVideo: "",
    status: "draft",
    upcoming: false,
  });

  const [errors, setErrors] = useState({});
  const [tagInput, setTagInput] = useState("");

  const categories = [
    { value: "music", label: "Music" },
    { value: "sports", label: "Sports" },
    { value: "theater", label: "Theater" },
    { value: "conference", label: "Conference" },
    { value: "workshop", label: "Workshop" },
    { value: "other", label: "Other" },
  ];

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.title.trim()) newErrors.title = "Event title is required";
      if (!formData.description.trim())
        newErrors.description = "Event description is required";
      if (!formData.category) newErrors.category = "Event category is required";
    }

    if (step === 2) {
      if (!formData.startDate) newErrors.startDate = "Start date is required";
      if (!formData.endDate) newErrors.endDate = "End date is required";
      if (!formData.time) newErrors.time = "Event time is required";

      if (formData.startDate && formData.endDate) {
        if (new Date(formData.startDate) >= new Date(formData.endDate)) {
          newErrors.endDate = "End date must be after start date";
        }
      }
    }

    if (step === 3) {
      if (!formData.location.venue.trim())
        newErrors.venue = "Venue is required";
      if (!formData.location.address.trim())
        newErrors.address = "Address is required";
      if (!formData.location.city.trim()) newErrors.city = "City is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleInputChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }));
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (isDraft = false) => {
    if (!validateStep(1) || !validateStep(2) || !validateStep(3)) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsLoading(true);

    try {
      const submitData = new FormData();

      // Add basic event data
      submitData.append("title", formData.title);
      submitData.append("description", formData.description);
      submitData.append("category", formData.category);
      submitData.append("startDate", formData.startDate);
      submitData.append("endDate", formData.endDate);
      submitData.append("time", formData.time);
      submitData.append("location", JSON.stringify(formData.location));
      submitData.append("maxAttendees", formData.maxAttendees);
      submitData.append("tags", JSON.stringify(formData.tags));
      submitData.append("status", isDraft ? "draft" : "published");

      if (formData.trailerVideo) {
        submitData.append("trailerVideo", formData.trailerVideo);
      }

      // Add images
      formData.images.forEach((image, index) => {
        submitData.append("images", image);
      });

      const response = await eventsAPI.createEvent(submitData);

      if (response.data) {
        toast.success(
          `Event ${isDraft ? "saved as draft" : "created"} successfully!`
        );
        if (onSuccess) {
          onSuccess(response.data);
        }
      }
    } catch (error) {
      console.error("Event creation error:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to create event. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Event Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => handleInputChange("title", e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0052CC] focus:border-transparent ${
            errors.title ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="Enter event title"
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-600">{errors.title}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Event Description <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          rows={4}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0052CC] focus:border-transparent ${
            errors.description ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="Describe your event..."
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Category <span className="text-red-500">*</span>
        </label>
        <select
          value={formData.category}
          onChange={(e) => handleInputChange("category", e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0052CC] focus:border-transparent ${
            errors.category ? "border-red-500" : "border-gray-300"
          }`}
        >
          <option value="">Select a category</option>
          {categories.map((cat) => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>
        {errors.category && (
          <p className="mt-1 text-sm text-red-600">{errors.category}</p>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Date & Time</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={formData.startDate}
            onChange={(e) => handleInputChange("startDate", e.target.value)}
            min={new Date().toISOString().split("T")[0]}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0052CC] focus:border-transparent ${
              errors.startDate ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.startDate && (
            <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            End Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={formData.endDate}
            onChange={(e) => handleInputChange("endDate", e.target.value)}
            min={formData.startDate || new Date().toISOString().split("T")[0]}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0052CC] focus:border-transparent ${
              errors.endDate ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.endDate && (
            <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Event Time <span className="text-red-500">*</span>
        </label>
        <input
          type="time"
          value={formData.time}
          onChange={(e) => handleInputChange("time", e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0052CC] focus:border-transparent ${
            errors.time ? "border-red-500" : "border-gray-300"
          }`}
        />
        {errors.time && (
          <p className="mt-1 text-sm text-red-600">{errors.time}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Maximum Attendees
        </label>
        <input
          type="number"
          value={formData.maxAttendees}
          onChange={(e) =>
            handleInputChange("maxAttendees", parseInt(e.target.value) || 100)
          }
          min="1"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0052CC] focus:border-transparent"
        />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">
        Location & Details
      </h3>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Venue <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.location.venue}
          onChange={(e) => handleInputChange("location.venue", e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0052CC] focus:border-transparent ${
            errors.venue ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="Enter venue name"
        />
        {errors.venue && (
          <p className="mt-1 text-sm text-red-600">{errors.venue}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Address <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.location.address}
          onChange={(e) =>
            handleInputChange("location.address", e.target.value)
          }
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0052CC] focus:border-transparent ${
            errors.address ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="Enter full address"
        />
        {errors.address && (
          <p className="mt-1 text-sm text-red-600">{errors.address}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          City <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.location.city}
          onChange={(e) => handleInputChange("location.city", e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0052CC] focus:border-transparent ${
            errors.city ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="Enter city"
        />
        {errors.city && (
          <p className="mt-1 text-sm text-red-600">{errors.city}</p>
        )}
      </div>

      <div className="flex items-center space-x-3">
        <input
          type="checkbox"
          id="upcoming"
          checked={formData.upcoming}
          onChange={(e) => handleInputChange("upcoming", e.target.checked)}
          className="w-4 h-4 text-[#0052CC] bg-gray-100 border-gray-300 rounded focus:ring-[#0052CC] focus:ring-2"
        />
        <label htmlFor="upcoming" className="text-sm font-medium text-gray-700">
          Show in upcoming events section
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tags
        </label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyPress={(e) =>
              e.key === "Enter" && (e.preventDefault(), handleAddTag())
            }
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0052CC] focus:border-transparent"
            placeholder="Add tags..."
          />
          <button
            type="button"
            onClick={handleAddTag}
            className="px-4 py-2 bg-[#0052CC] text-white rounded-lg hover:bg-[#003d99] transition-colors"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.tags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
            >
              {tag}
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                className="ml-2 text-blue-600 hover:text-blue-800"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Event Images
        </label>
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleImageUpload}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0052CC] focus:border-transparent"
        />
        {formData.images.length > 0 && (
          <div className="mt-2 grid grid-cols-3 gap-2">
            {formData.images.map((image, index) => (
              <div key={index} className="relative">
                <img
                  src={URL.createObjectURL(image)}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-20 object-cover rounded"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveImage(index)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Trailer Video URL (optional)
        </label>
        <input
          type="url"
          value={formData.trailerVideo}
          onChange={(e) => handleInputChange("trailerVideo", e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0052CC] focus:border-transparent"
          placeholder="https://..."
        />
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Create New Event
        </h2>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step <= currentStep
                    ? "bg-[#0052CC] text-white"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                {step}
              </div>
              {step < 3 && (
                <div
                  className={`w-16 h-1 mx-2 ${
                    step < currentStep ? "bg-[#0052CC]" : "bg-gray-200"
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={(e) => e.preventDefault()}>
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <div>
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handlePrevious}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Previous
              </button>
            )}
          </div>

          <div className="flex gap-3">
            {currentStep === 3 ? (
              <>
                <button
                  type="button"
                  onClick={() => handleSubmit(true)}
                  disabled={isLoading}
                  className="px-6 py-2 border border-[#0052CC] text-[#0052CC] rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
                >
                  Save as Draft
                </button>
                <button
                  type="button"
                  onClick={() => handleSubmit(false)}
                  disabled={isLoading}
                  className="px-6 py-2 bg-[#0052CC] text-white rounded-lg hover:bg-[#003d99] transition-colors disabled:opacity-50"
                >
                  {isLoading ? "Creating..." : "Create Event"}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                className="px-6 py-2 bg-[#0052CC] text-white rounded-lg hover:bg-[#003d99] transition-colors"
              >
                Next
              </button>
            )}
          </div>
        </div>

        {onCancel && (
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              Cancel
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default EventCreationForm;
